import {AccessibilityInfo, Platform} from 'react-native';
import Toast from 'react-native-toast-message';
import {autorun, makeAutoObservable, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import {ERROR_MESSAGE, SUCCESS_CHECK, SUCCESS_TEXT} from '../components/ToastBottom';
import {GiftCardFormData} from '../giftCardShop/screens/schema';
import {AirtablePromotionItem} from '../models/airtable';
import {AppRoute} from '../models/AppRoute';
import {
  Address,
  CreateZenGiftCardPaymentInstrumentRequest,
  CreateZenGiftCardPaymentInstrumentResponse,
  ZenGiftCardsPaymentInstrument,
} from '../models/mfl';
import {GiftCardRecipientData} from '../models/middleware';
import * as RootNavigation from '../navigation/RootNavigation';
// Deliberately NOT the `queries/promotions` barrel: the barrel re-exports
// `usePromotions`, which imports `stores/use-stores` -> `stores/rootStore`,
// which imports this file — a cycle `npm run check:circular` fails on.
import {ensurePromotions} from '../queries/promotions/promotionsAccess';
import {type OrderProcessingScreenProps} from '../screens/OrderProcessingScreen';
import apiService from '../transport/apiService';
import {
  Day,
  DeliveryTimesResponse,
  OrderLine,
  OrderResponse,
  PickupSlot,
  PickupTimesResponse,
} from '../transport/apiServiceResponse';
import {Analytics} from '../utils/analytics/analytics';
import {EventName, EventScreenName} from '../utils/analytics/events';
import {logError} from '../utils/errorLogging';
import {sum} from '../utils/functions';
import {isSummerCashGrabCompetition} from '../utils/games/summerCashGrab';
import {ZenGiftCardsPaymentInstrumentWithChargedAmount} from '../utils/giftCards';
import {logger, serializeError} from '../utils/logger';
import {isUnhandledMFL500Error} from '../utils/mfl';
import {
  calculateSubtotal,
  getDiscountsAndCredits,
  getNonDeliveryAdjustments,
  isDeliveryFee,
  isPickupFee,
  type OrderAdjustment,
} from '../utils/orderCalculations';
import {isString} from '../utils/string';

import {parseCheckoutError} from './utils/checkoutErrorRecovery';
import {findCouponError, isCouponRefusalCode} from './utils/couponValidation';
import type {RootStore} from './rootStore';

const UPDATE_DEBOUNCE_TIME = 1250;

// DIS-1608: matches the cart's own write debounce — a burst of quantity taps
// produces one validate call, not one per tap.
const COUPON_CHECK_DEBOUNCE_TIME = UPDATE_DEBOUNCE_TIME;

// DIS-1608: announced when the basket crosses INTO qualifying. MFL says nothing
// on a clean validate, so this sentence is ours.
const COUPON_QUALIFIED_MESSAGE = 'Coupon applied to your order.';
let timeout: NodeJS.Timeout | null = null;

// Delivery notes on who is accepting the delivery. Gets added to the order
// delivery instructions, but stripped back out from the notes for display.
const REGEX_DELIVERY_WORDING = /\*{2}(ACCEPTING )?DELIVER(Y)?( TO)?:.*\*{2}/;
const NOMINATED_DELIVERY = 'ACCEPTING DELIVERY: ';
const SELF_DELIVERY = 'DELIVER TO: ';
const CHILL_DRINKS = '**CHILL DRINKS**\n';

type OrderUpdate = {
  productId: string;
  quantity: number;
};

const getOrderTotalAmount = (order: OrderResponse, switchingDeliveryPickup: boolean) => {
  let orderTotalAmount = 0;
  if (!switchingDeliveryPickup) {
    orderTotalAmount = order?.balance_at_checkout || 0;
  } else {
    orderTotalAmount =
      order?.order_lines
        ?.filter((ol: OrderLine) => !(isDeliveryFee(ol) || isPickupFee(ol)))
        .map((ol: OrderLine) => ol.subtotal?.cents_inc_tax)
        .reduce(sum, 0) || 0;
    if (order?.adjustments) {
      orderTotalAmount +=
        order.adjustments
          ?.filter(
            (adj: OrderAdjustment) => !!adj.name && !adj.name.toLowerCase().includes('delivery'),
          )
          .map((adj: OrderAdjustment) => adj.amount.cents_inc_tax)
          .reduce(sum, 0) || 0;
    }
  }
  return orderTotalAmount;
};

const readjustGiftCardsChargeAmounts = (
  order: OrderResponse,
  zenGiftCardsUserPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[],
  isSwitchingDeliveryPickup: boolean,
) => {
  let remaining = getOrderTotalAmount(order, isSwitchingDeliveryPickup);

  const giftCardsWithReadjustedChargeAmounts = zenGiftCardsUserPaymentInstruments.map(card => {
    const chargedAmount = Math.min(card.balance, remaining);
    remaining = Math.max(0, remaining - chargedAmount);
    return {
      ...card,
      chargedAmount,
    };
  });
  return giftCardsWithReadjustedChargeAmounts;
};

const getZenGiftCardRecipientFormValuesFromOrder = (order: OrderResponse) => {
  if (!order?.gift_information) return null;

  const {recipient_name, recipient_email_address, recipient_phone_number, note, gift_email_date} =
    order.gift_information;

  if (
    recipient_name &&
    recipient_email_address &&
    recipient_phone_number &&
    note &&
    gift_email_date
  ) {
    return {
      recipientName: recipient_name,
      recipientEmail: recipient_email_address,
      recipientPhoneNumber: recipient_phone_number,
      deliveryDate: new Date(gift_email_date),
      note,
    };
  }

  return null;
};

export type CouponStatus = 'none' | 'checking' | 'qualified' | 'unqualified';

/**
 * DIS-1608: the outcome of applying a coupon. `{ok: false}` means the code never
 * stayed attached — a coupon refusal. `{ok: true, qualified: false}` means it
 * attached and the cart's coupon row now carries its status.
 */
export type CouponApplyResult = {ok: true; qualified: boolean} | {ok: false; message: string};

export class CartStore {
  /**
   * callback to run after updatedorder
   */
  private _afterUpdate: (() => void) | null = null;
  rootStore: RootStore | null = null;
  currentOrder: OrderResponse | null = null;
  // DIS-1608: MFL accepts a coupon whose minimum spend the basket does not meet,
  // and only refuses it at validate. Held so the refusal survives the modal close
  // and stays on the cart coupon row while the shopper adds items.
  couponIssue: {
    code: string | null;
    detail: string;
    orderSignature: string;
  } | null = null;
  /**
   * DIS-1608: whether the last validate pre-check that actually resolved said the
   * coupon qualifies. Distinguishes "re-checking a coupon that was working a moment
   * ago" from "never asked about this coupon", which is the difference between
   * holding the displayed total steady and suppressing the discount. False until a
   * check resolves, so a brand-new coupon is never shown a discount on trust.
   */
  couponLastSettledQualified = false;
  /**
   * DIS-1608: true for the whole of an `updateCoupon` call — the apply, its
   * reload and the pre-check that follows. Deliberately separate from
   * `couponCheckPending`: that flag is owned by the debounced re-check, and
   * `refreshCouponStatus` clears it on any order without a coupon. The reload
   * inside `updateCoupon` passes through exactly that state, so sharing one flag
   * let the apply's own reload clear it and report 'qualified' on an unverified
   * coupon. Measured by an autorun over `couponStatus`.
   */
  couponApplyInFlight = false;
  /**
   * DIS-1608: true from the moment a validate pre-check is queued until it
   * resolves — the debounce window included, or `couponStatus` would report
   * 'qualified' for an unchecked coupon for a whole debounce period. Distinguishes
   * "no refusal held because MFL said the basket qualifies" from "no refusal held
   * because we have not asked yet" — the display suppresses the discount in both
   * cases, but only the first is 'qualified'.
   */
  couponCheckPending: boolean = false;
  submittingOrder: boolean = false;
  recentOrders: OrderResponse[] = [];
  isLoadingOrder: boolean = true;

  // This is a buffer of update to order lines that will
  // get synced off to MFL periodically.
  orderUpdate: OrderUpdate[] = []; // products/qty to update current order with
  dirtyOrder: boolean = false; // details have changed but not submitted yet
  updatingOrder: boolean = false; // update sent to MFL waiting response
  loadingRecentOrders: boolean = true; // recent orders for user are loading
  orderProcessing: boolean = false; // order has been submitted to MFL waiting for checkout state
  orderCheckedOutSuccess: boolean = false;
  orderFailedPayment: boolean = false;
  orderPaymentIncomplete: boolean = false;
  orderPayment3DSChallenged: boolean = false;

  /**
   * DIS-1420: id of the order we have already dispatched a terminal post-checkout
   * navigation for. pollOrderStatus is polled fire-and-forget every 2s; this keys
   * the navigate-once guard so overlapping polls of the same succeeded order cannot
   * fire a second terminal navigation (a duplicate tab/cross-navigator switch that
   * compounds the react-native-screens Fabric reparent crash). The 3DS interim
   * navigate is intentionally NOT counted (different branch), so a 3DS->resume->
   * success still navigates exactly once. Non-observable (nothing renders off it).
   */
  private _navigatedOrderId: string | null = null;
  private _couponCheckTimeout: NodeJS.Timeout | null = null;

  // Eligible competitions
  eligibleCompetitions: AirtablePromotionItem[] = [];

  // Timeslots
  days: Day[] = [];
  selectedDay: Day | null = null;
  selectedSlot: ReturnType<typeof this.transformMFLSlot> | null = null;
  selectedSlotDirty: boolean = false;
  pickupSlots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> = {};
  loadingDeliverySlots: boolean = true;
  /**
   * if noDeliverySlotsAvailable it indicates address is not in a serviceable area for that store
   */
  noDeliverySlotsAvailable: boolean = false;
  deliverySlots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> = {};
  loadingPickupSlots: boolean = true;
  switchingDeliveryPickup: boolean = false; // Do we hide delivery/pickup charge from display

  // Order details from user
  isDelivery: boolean = false;
  isPickup: boolean = true;
  deliveryAddress: Address | null = null;
  deliveryAddressDirty: boolean = false;
  deliveryRegion: {id: string; name: string} | null = null;
  deliveryInstructions: string = '';
  deliveryInstructionsDirty: boolean = false;
  _orderNotes: string = '';
  chillDrinks: boolean = false;

  // Nominated person for delivery
  willBePresent: boolean = false;
  hasNominatedPerson: boolean = false;
  nominatedPersonName: string = '';
  nominatedPersonDirty: boolean = false;

  // Force a loading of region from delivery address when first loading the order
  forceLoadRegion: boolean = true;

  // Products promoted at checkout
  promotedAtCheckoutProducts: unknown = null;

  // Running total of the order
  totalAmount: number = 0; // cents

  // Gift Card orders
  zenGiftCardRecipientFormValues: GiftCardFormData | null = null;
  zenGiftCardsUserPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[] = [];

  /**
   * activated exclusive offers
   */
  activatedDeals: string[] = [];

  /**
   * Set to true when an expired offer is detected during validation.
   * OrderTotals observes this to show inline warning banner.
   */
  expiredOfferDetected = false;

  /**
   * Tracks if we've already attempted offer recovery this checkout session.
   * Prevents infinite loop if clearing deals doesn't fix the issue.
   * Reset when user starts new checkout or navigates away.
   */
  recoveryAttemptedThisSession = false;

  /**
   * Whether an activated deal has expired on the server.
   *
   * Only true when the server explicitly tells us via 422 clear_deals
   * response during validateOrder(). We do NOT use canApply (always false,
   * DIS-697 never implemented) or discounts_and_credits (unreliable from MFL).
   *
   * This is the ONLY reliable signal for an expired exclusive offer.
   */
  get activeDealInvalid(): boolean {
    return this.expiredOfferDetected;
  }

  /**
   * Identifies the basket the coupon refusal was measured against. Line contents
   * are included, not just the count: swapping a non-qualifying line for a
   * qualifying one at the same price and count is exactly the change the refusal
   * asks for, and must invalidate the message.
   *
   * Fee lines participate too: switching delivery to pickup adds or removes the
   * fee line and clears the coupon message even though the qualifying items did
   * not change. That fails in the safe direction — the message vanishes rather
   * than lying — so it is intentional, not a bug.
   */
  get couponOrderSignature() {
    const order = this.currentOrder;
    const lines = (order?.order_lines ?? [])
      .map(line => `${line.product_id}:${line.quantity}`)
      .sort()
      .join(',');
    return `${order?.coupon_code ?? ''}|${order?.total?.cents_inc_tax ?? 0}|${lines}`;
  }

  /**
   * DIS-1608: valid / qualified / applied per CONTEXT.md. 'checking' is the honest
   * answer whenever MFL has not judged THIS basket: either a check is in flight, or
   * the held refusal was measured against a basket that has since changed. The
   * display treats 'checking' like 'unqualified' — full price until earned — while
   * only 'unqualified' blocks checkout, so a debounced re-check never flickers the
   * button.
   */
  get couponStatus(): CouponStatus {
    if (!this.currentOrder?.coupon_code) return 'none';
    if (this.couponIssue) {
      return this.couponIssue.orderSignature === this.couponOrderSignature
        ? 'unqualified'
        : 'checking';
    }
    return this.couponCheckPending || this.couponApplyInFlight ? 'checking' : 'qualified';
  }

  /**
   * DIS-1608: a re-check is running for a coupon whose last resolved answer was
   * "qualifies". The display holds the settled figures and shows a progress
   * indicator rather than adding the discount back, because every cart write
   * queues a check and the add-back would otherwise jump the total up and back
   * down on every quantity tap.
   */
  get couponRecheckInFlight(): boolean {
    return this.couponStatus === 'checking' && this.couponLastSettledQualified;
  }

  /** The coupon code whose discount must not be counted, or null. */
  get uncountedCouponCode(): string | null {
    if (this.couponStatus === 'qualified' || this.couponStatus === 'none') return null;
    // Re-checking something that was qualifying: keep counting it until the answer
    // lands. couponBlocksCheckout is false throughout 'checking' either way, so
    // this trades no checkout safety for a stable total.
    if (this.couponRecheckInFlight) return null;
    return this.currentOrder?.coupon_code ?? null;
  }

  get couponBlocksCheckout(): boolean {
    return this.couponStatus === 'unqualified';
  }

  /**
   * Whether a specific offer (by MFL ID) is currently applied to the cart.
   *
   * Uses activatedDeals as single source of truth. Does NOT fall back to
   * offer.isActivated — eliminates the brief "APPLIED" flash after removal
   * that the stale isActivated field caused (Issue #8).
   */
  isOfferApplied(mflId: string): boolean {
    return this.activatedDeals.includes(mflId);
  }

  reset = () =>
    runInAction(() => {
      this.currentOrder = null;
      this.days = [];
      this.deliveryAddress = null;
      this.deliveryAddressDirty = false;
      this.deliveryInstructions = '';
      this.deliveryInstructionsDirty = false;
      this.deliveryRegion = null;
      this.deliverySlots = {};
      this.dirtyOrder = false;
      this.eligibleCompetitions = [];
      this.hasNominatedPerson = false;
      this.isDelivery = false;
      this.isLoadingOrder = true;
      this.isPickup = true;
      this.loadingDeliverySlots = true;
      this.loadingPickupSlots = true;
      this.loadingRecentOrders = true;
      this.nominatedPersonDirty = false;
      this.nominatedPersonName = '';
      this.orderCheckedOutSuccess = false;
      this.orderFailedPayment = false;
      this.orderNotes = '';
      this.orderPayment3DSChallenged = false;
      this.orderPaymentIncomplete = false;
      this.orderProcessing = false;
      this.orderUpdate = [];
      this.pickupSlots = {};
      this.promotedAtCheckoutProducts = null;
      this.recentOrders = [];
      this.selectedDay = null;
      this.selectedSlot = null;
      this.selectedSlotDirty = false;
      this.submittingOrder = false;
      this.switchingDeliveryPickup = false;
      this.totalAmount = 0;
      this.updatingOrder = false;
      this.willBePresent = false;
      this.zenGiftCardRecipientFormValues = null;
      this.zenGiftCardsUserPaymentInstruments = [];
      this.chillDrinks = false;
      this.activatedDeals = [];
      this.expiredOfferDetected = false;
      this.recoveryAttemptedThisSession = false;
      this._afterUpdate = null;
      this._navigatedOrderId = null; // DIS-1420: clear navigate-once guard on full reset
      // DIS-1608: a queued check belongs to the order being discarded — it would
      // otherwise fire against whatever session replaces it.
      if (this._couponCheckTimeout) clearTimeout(this._couponCheckTimeout);
      this._couponCheckTimeout = null;
      this.couponIssue = null;
      this.couponCheckPending = false;
      this.couponApplyInFlight = false;
      this.couponLastSettledQualified = false;
    });

  /**
   * This set from api payload.
   * Its intended that this._orderNotes not contain the chill drinks message
   */
  set orderNotes(value) {
    runInAction(() => {
      this.chillDrinks = (value || '').indexOf(CHILL_DRINKS) > -1;
      this._orderNotes = (value || '').replaceAll(CHILL_DRINKS, '');
    });
  }

  /**
   * Returns orderNotes without chill drinks. This is intended when displaying to customer so shouldn't contain chill drinks message.
   */
  get orderNotes() {
    return (this._orderNotes || '').replaceAll(CHILL_DRINKS, '');
  }

  debounceUpdate = () => {
    const later = () => {
      timeout = null;
      this.updateOrder();
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, UPDATE_DEBOUNCE_TIME);
  };

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {
      isOfferApplied: false, // plain method — observable reads inside are tracked by MobX observers
      _navigatedOrderId: false, // DIS-1420 navigate-once guard — plain field, not observable
      _couponCheckTimeout: false, // DIS-1608 debounce handle — plain field, not observable
    });
    makeLoggable(this);

    // Update order on changes to lines
    autorun(() => {
      if (this.orderUpdate.length) {
        this.debounceUpdate();
      }
    });

    // Update particular details in our store once an order is loaded
    autorun(() => {
      if (this.currentOrder) {
        // Don't show delivery/pickup charge line if it exists on the order and doesn't match the
        // selected option in the store
        const hasDeliveryCharge = !!this.currentOrder?.order_lines?.find(isDeliveryFee);
        const hasPickupCharge = !!this.currentOrder?.order_lines?.find(isPickupFee);

        // We don't want to be updating again while we're reading back the values
        // of the order we just got back from an API call.
        runInAction(() => {
          this.selectedSlotDirty = false;
          this.deliveryAddressDirty = false;
          this.nominatedPersonDirty = false;
          this.deliveryInstructionsDirty = false;
        });

        runInAction(() => {
          this.orderNotes = this.currentOrder?.note;
          if (this.currentOrder.delivery_address) {
            this.deliveryAddress = this.currentOrder.delivery_address;
            if (this.currentOrder.delivery_address?.instructions) {
              // Handle delivery person notes. Strip out the person's name
              // and type of delivery, set the appropriate delivery type
              // and nominated person's name if applicable.
              let name;
              let type;
              const parts = this.currentOrder.delivery_address.instructions.split('\n');
              // Pop mobile line first if present (new format — it's always last)
              let mobile = '';
              const mobileMatch = parts[parts.length - 1]?.match(/^Nominated person mobile: (.+)$/);
              if (mobileMatch) {
                parts.pop();
                mobile = mobileMatch[1].trim();
              }
              if (parts[parts.length - 1]?.match(REGEX_DELIVERY_WORDING)) {
                name = parts.pop();
                name = name.replaceAll('*', '');
                // nominated delivery person, don't care about self deliveries.
                if (name.startsWith(NOMINATED_DELIVERY)) {
                  name = name.replaceAll(NOMINATED_DELIVERY, '');
                  type = NOMINATED_DELIVERY;
                }
              }
              if (type === NOMINATED_DELIVERY) {
                if (name.includes('\t')) {
                  // Legacy format: name is already tab-delimited (pre-fix orders)
                  this.nominatedPersonName = name;
                } else {
                  // New format: split on space to reconstruct tab-delimited storage
                  const nameParts = name.split(' ');
                  const first = nameParts[0] || '';
                  const last = nameParts.slice(1).join(' ') || '';
                  this.nominatedPersonName = `${first}\t${last}\t${mobile}`;
                }
                this.hasNominatedPerson = true;
                this.willBePresent = false;
              } else {
                this.willBePresent = true;
                this.hasNominatedPerson = false;
              }
              const instructions = parts.filter((p: string) => !!p).join('\\n');
              if (instructions.length) {
                this.deliveryInstructions = instructions;
              }
            }
          }
          if (this.currentOrder.time_slot) {
            const [from, to] = [...this.currentOrder.time_slot.checkout_window];
            const fromDate = Date.parse(from);
            const toDate = Date.parse(to);
            const now = new Date().getTime();

            // Only apply time_slot settings if the checkout window is still valid
            // DIS-643: If slot is expired, leave isPickup/isDelivery at reset defaults
            // so that appropriate slots can be loaded for user to select
            if (fromDate < now && now < toDate) {
              this.selectedDay = this.currentOrder.time_slot.date;
              this.selectedSlot = this.transformMFLSlot(this.currentOrder.time_slot);

              if (this.currentOrder.time_slot.delivery_zone.type.toLowerCase() === 'pickup') {
                this.isPickup = true;
                this.isDelivery = false;
                this.switchingDeliveryPickup = hasDeliveryCharge;
              } else {
                this.isPickup = false;
                this.isDelivery = true;
                this.switchingDeliveryPickup = hasPickupCharge;
              }
            }
          }
          if (this.currentOrder.activated_deals) {
            this.activatedDeals = this.currentOrder.activated_deals;
            // DIS-1220: server-truth says no deals remain — banner is stale, drop it.
            if (this.activatedDeals.length === 0 && this.expiredOfferDetected) {
              this.expiredOfferDetected = false;
            }
          }
        });

        if (this.forceLoadRegion && this.currentOrder.delivery_address) {
          runInAction(() => (this.forceLoadRegion = false));
          this.loadRegionForPostCode();
        }
      }
    });

    // Keep a running total for display, keeping in mind that the user may be switching between
    // pickup and delivery
    autorun(() => {
      const orderTotalAmount = getOrderTotalAmount(this.currentOrder, this.switchingDeliveryPickup);
      runInAction(() => {
        this.totalAmount = orderTotalAmount;
      });
    });

    // Update delivery details on order when we have enough information to save to MFL.
    autorun(() => {
      // The info we care about for delivery details (instructions are optional, so don't bother checking them here)
      if (
        this.isDelivery &&
        this.deliveryAddress &&
        this.selectedSlot &&
        (this.willBePresent || (this.hasNominatedPerson && this.nominatedPersonName))
      ) {
        //  Are they actually dirty and need to be updated
        if (
          this.deliveryAddressDirty ||
          this.selectedSlotDirty ||
          this.nominatedPersonDirty ||
          this.deliveryInstructionsDirty
        ) {
          this.updateDeliveryDetails();
        }
      }
    });

    // Update detail on pick up changes
    autorun(() => {
      if (this.isPickup && this.selectedSlot && this.selectedSlotDirty) {
        this.updateDeliveryDetails();
      }
    });

    // Load the delivery region whenever the delivery address is changed
    autorun(() => {
      if (this.deliveryAddress && this.deliveryAddressDirty) {
        this.loadRegionForPostCode();
      }
    });
  }

  setDelivery = (clear: boolean) => {
    const switching = !!this.currentOrder?.order_lines?.find(isPickupFee);
    runInAction(() => {
      this.isDelivery = true;
      this.isPickup = false;
      this.switchingDeliveryPickup = switching;
      if (clear) {
        this.selectedSlot = null;
      }
    });
  };

  setPickup = (clear: boolean) => {
    const isSwitchingDeliveryPickup = !!this.currentOrder?.order_lines?.find(isDeliveryFee);
    runInAction(() => {
      this.isDelivery = false;
      this.isPickup = true;
      this.switchingDeliveryPickup = isSwitchingDeliveryPickup;
      if (clear) {
        this.selectedSlot = null;
      }
    });
  };

  // Transform variable names AND strip timezones for display. We're not interested in
  // getting the local date/time.
  transformMFLSlot = (slot: PickupSlot) => {
    return {
      dropOffWindow: slot.drop_off_window.map((time: string) => time.split('+')[0]),
      checkoutWindow: slot.checkout_window.map((time: string) => time.split('+')[0]),
      id: slot.id,
      date: slot.date,
    };
  };

  loadPickupSlots = () => {
    runInAction(() => (this.loadingPickupSlots = true));
    apiService
      .getPickupTimeSlots()
      .then((data: unknown) => {
        const slots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> = {};
        (data as PickupTimesResponse).forEach(slot => {
          if (!slots[slot.date]) {
            slots[slot.date] = [];
          }
          slots[slot.date].push(this.transformMFLSlot(slot));
        });
        runInAction(() => {
          this.days = Object.keys(slots) as Day[];
          this.pickupSlots = slots;
          this.loadingPickupSlots = false;
        });
      })
      .catch(err => {
        runInAction(() => {
          this.days = [];
          this.selectedDay = null;
          this.pickupSlots = {};
          this.loadingPickupSlots = false;
        });
        logError(err, 'CartStore.loadPickupSlots');
      });
  };

  loadDeliverySlots = () => {
    runInAction(() => {
      this.noDeliverySlotsAvailable = false;
      this.loadingDeliverySlots = true;
    });
    apiService
      .getDeliveryTimeSlots(this.deliveryRegion.id)
      .then((data: unknown) => {
        logger.debug('CartStore', 'Loaded delivery slots', {
          regionId: this.deliveryRegion.id,
          slotsCount: (data as DeliveryTimesResponse)?.length,
        });
        if (!(data as DeliveryTimesResponse).length) {
          runInAction(() => {
            this.days = [];
            this.selectedDay = null;
            this.deliverySlots = {};
            this.loadingDeliverySlots = false;
            this.noDeliverySlotsAvailable = true;
          });
        } else {
          const slots: Record<Day, ReturnType<typeof this.transformMFLSlot>[]> = {};
          (data as DeliveryTimesResponse).forEach(slot => {
            if (!slots[slot.date]) {
              slots[slot.date] = [];
            }
            slots[slot.date].push(this.transformMFLSlot(slot));
          });
          runInAction(() => {
            this.days = Object.keys(slots) as Day[];
            this.deliverySlots = slots;
            this.loadingDeliverySlots = false;
          });
        }
      })
      .catch(_err => {
        runInAction(() => {
          this.days = [];
          this.selectedDay = null;
          this.deliverySlots = {};
          this.loadingDeliverySlots = false;
        });
        logError(_err, 'CartStore.loadDeliverySlots');
      });
  };

  selectDay = (day: Day | null) => (this.selectedDay = day);

  selectSlot = (slot: ReturnType<typeof this.transformMFLSlot> | null, callback?: () => void) =>
    runInAction(() => {
      this.selectedSlot = slot;
      this.selectedSlotDirty = true;
      if (callback) {
        this._afterUpdate = callback;
      }
    });

  setDeliveryAddress = (address: Address) =>
    runInAction(() => {
      this.noDeliverySlotsAvailable = false;
      this.deliveryAddress = address;
      this.deliveryAddressDirty = true;
    });

  loadRegionForPostCode = () =>
    apiService
      .getRegions(this.deliveryAddress?.postal_code)
      .then((data: {id: string; name: string}[]) => {
        logger.debug('CartStore', 'Loaded delivery regions', {
          postalCode: this.deliveryAddress?.postal_code,
          regionsCount: data?.length,
        });
        const region = data.find(
          (d: {name: string}) => d.name === this.deliveryAddress?.locality.toUpperCase(),
        );
        if (region) {
          runInAction(() => {
            this.deliveryRegion = region;
          });
        } else {
          logger.warn('CartStore', 'Unable to match delivery region to delivery suburb', {
            postalCode: this.deliveryAddress?.postal_code,
            locality: this.deliveryAddress?.locality,
          });
        }
      })
      .catch(err => logError(err, 'CartStore.setDeliveryAddress.getRegion'));

  setDeliveryInstructions = (instructions: string) =>
    runInAction(() => {
      this.deliveryInstructions = instructions;
      this.deliveryInstructionsDirty = true;
    });

  /**
   * This is set from UI. won't include chill drinks in notes
   * @param notes
   * @returns
   */
  setOrderNotes = (notes: string) => runInAction(() => (this._orderNotes = notes));

  setWillBePresent = (value: boolean) =>
    runInAction(() => {
      this.willBePresent = value;
      this.nominatedPersonDirty = true;
    });

  // Only set dirty if the name is present.
  setHasNominatedPerson = (value: boolean) => {
    if (this.nominatedPersonName) {
      runInAction(() => {
        this.hasNominatedPerson = value;
        this.nominatedPersonDirty = true;
      });
    } else {
      runInAction(() => (this.hasNominatedPerson = value));
    }
  };

  setNominatedPersonName = (value: string) =>
    runInAction(() => {
      this.nominatedPersonName = value;
      this.nominatedPersonDirty = true;
    });

  /**
   * Get nominated person as firstName, lastName, mobile
   */
  get nominatedPersonParts() {
    const parts = this.nominatedPersonName.split('\t');
    return {
      firstName: parts?.[0],
      lastName: parts?.[1],
      mobile: parts?.[2],
    };
  }

  /**
   * This can be used instead of setNominatedPersonName if you want to set first last and mobile the parts will be tab delimited
   * see getter nominatedPersonParts
   * @param param0
   */
  setNominatedPersonParts({
    firstName,
    lastName,
    mobile,
  }: {
    firstName: string;
    lastName: string;
    mobile: string;
  }) {
    this.setNominatedPersonName(`${firstName}\t${lastName}\t${mobile}`);
  }

  updateGiftCardRecipientDetails = async (giftCardRecipientData: GiftCardRecipientData) =>
    apiService.updateGiftCardRecipientInformation(giftCardRecipientData);

  /**
   * Set chill drinks from UI
   * @param value
   */
  setChillDrinks(value: boolean) {
    runInAction(() => (this.chillDrinks = value));
    this.updateOrderNotes();
  }

  get orderNotesWithChillDrinks() {
    return (this.chillDrinks ? CHILL_DRINKS : '') + this.orderNotes;
  }

  updateOrderNotes = () => {
    if (this.orderNotesWithChillDrinks !== this.currentOrder?.note) {
      const order = {
        note: this.orderNotesWithChillDrinks,
      };
      apiService
        .updateOrderNote(order)
        .then((res: {data: OrderResponse}) => runInAction(() => (this.currentOrder = res.data)))
        .catch(_err => logError(_err, 'CartStore.setOrderNote'));
    }
  };

  // We can save an address or instructions only for a delivery and only if a timeslot is selected.
  // Pickup doesn't have any address or instructions, so we can get away with just the timeslot.
  // I don't really want to double call the API, but we have to force validation as
  // the order lines (especially the pickup/delivery fees) aren't recalculated.
  //
  // IMPORTANT: This method now properly awaits validateOrderAsync() before calling the callback.
  // This fixes a race condition where the callback (e.g., closing the time slot modal) would fire
  // before the order validation completed, causing the cart to show "NO PRODUCTS FOUND" momentarily.
  updateDeliveryDetails = async () => {
    interface DeliveryUpdatePayload {
      time_slot_id?: string;
      delivery_address?: Address;
      instructions?: string;
      delivery_recipient_name?: string;
    }

    const order: DeliveryUpdatePayload = {};
    if (this.selectedSlot) {
      order.time_slot_id = this.selectedSlot.id;
    }
    if (this.isDelivery) {
      if (this.deliveryAddress && this.selectedSlot) {
        order.delivery_address = this.deliveryAddress;
      }
      // Delivery instructions won't be accepted without a delivery slot having been chosen.
      if (this.deliveryInstructions && this.selectedSlot) {
        order.instructions = this.deliveryInstructions;
        order.instructions += this.deliveryInstructions.length ? '\n' : '';
      }
      // Add nominated person to delivery instructions.
      if (this.willBePresent && this.selectedSlot) {
        order.instructions = order.instructions || '';
        order.instructions += `**${SELF_DELIVERY}${this.rootStore?.userStore.firstName} ${this.rootStore?.userStore.lastName}**`;
      } else if (this.nominatedPersonName && this.selectedSlot) {
        order.instructions = order.instructions || '';
        const {firstName, lastName, mobile} = this.nominatedPersonParts;
        order.instructions += `**${NOMINATED_DELIVERY}${firstName} ${lastName}**`;
        if (mobile) {
          order.instructions += `\nNominated person mobile: ${mobile}`;
        }
        order.delivery_recipient_name = `${firstName} ${lastName}`;
      }
    }
    // Don't update if it's empty
    if (Object.keys(order).length) {
      try {
        await apiService.updateDeliveryDetails(order);
        // Reload order to get fresh data (e.g., updated totals) before modal closes
        // Note: validateOrder() is called later in handleContinue() before payment
        await this.loadOrderForUser();
      } catch (err) {
        logError(err, 'CartStore.updateDeliveryDetails');
      } finally {
        runInAction(() => {
          this.selectedSlotDirty = false;
          this.deliveryAddressDirty = false;
          this.nominatedPersonDirty = false;
          this.deliveryInstructionsDirty = false;
        });
        if (this._afterUpdate) {
          this._afterUpdate?.();
          this._afterUpdate = null;
        }
      }
    } else {
      if (this._afterUpdate) {
        this._afterUpdate?.();
        this._afterUpdate = null;
      }
    }
  };

  fullyReloadOrderFromMFL = () => {
    this.reset();
    return this.loadOrderForUser();
  };

  loadOrderForUser = async () => {
    try {
      runInAction(() => {
        this.isLoadingOrder = true;
        this.forceLoadRegion = true;
      });
      const data = await apiService.loadCurrentOrder();
      runInAction(() => {
        this.currentOrder = data;
        this.dirtyOrder = false;
        this.zenGiftCardRecipientFormValues = getZenGiftCardRecipientFormValuesFromOrder(data);
      });
      // DIS-1608 trigger 8: the other seven triggers are order writers, so a plain
      // reload — including the one that follows sign-in — used to show a stale
      // `applied` label. refreshCouponStatus returns without a request when the
      // loaded order has no coupon and no activated deal, so the other load paths
      // pay nothing for this.
      this.refreshCouponStatus();
    } catch (_error) {
      logger.error('CartStore', 'Unable to load order for user', serializeError(_error));
    }
    runInAction(() => (this.isLoadingOrder = false));
  };

  loadRecentOrders = () => {
    runInAction(() => {
      this.loadingRecentOrders = true;
      this.recentOrders = [];
    });
    apiService
      .loadRecentOrders()
      .then(data => {
        runInAction(() => {
          this.loadingRecentOrders = false;
          this.recentOrders = data
            .filter((o: OrderResponse) => !!o.order_number)
            .sort(
              (a: OrderResponse, b: OrderResponse) =>
                Number(b.order_number) - Number(a.order_number),
            );
        });
      })
      .catch(_err => {
        runInAction(() => {
          this.loadingRecentOrders = false;
          this.recentOrders = [];
        });
        logger.error('CartStore', 'Unable to load recent orders for user', serializeError(_err));
      });
  };

  setItemQty = (productId: string, quantity: number) => {
    const orderUpdate = [...this.orderUpdate];
    const currentOrderItem = orderUpdate.find(item => item.productId === productId);
    if (currentOrderItem) {
      currentOrderItem.quantity = quantity;
    } else {
      orderUpdate.push({productId: productId, quantity: quantity});
    }
    runInAction(() => {
      this.orderUpdate = orderUpdate;
      this.dirtyOrder = true;
    });
  };

  getItemQty = (itemId: string) => {
    const found = this.currentOrder?.order_lines?.find(
      (line: OrderLine) => line.product_id === itemId,
    );
    return found?.quantity || 0;
  };

  getTotalOrderCount = () => {
    // Sum up the lines on the order, ignore charges and anything that is
    // in the orderUpdate object.
    const orderTotal =
      this.currentOrder?.order_lines
        ?.filter(
          (ol: OrderLine) =>
            !ol.charge && !this.orderUpdate.find(col => col.productId === ol.product_id),
        )
        .map((ol: OrderLine) => ol.quantity)
        .reduce((a: number, b: number) => a + b, 0) || 0;

    const updateTotals = this.orderUpdate?.map(ou => ou.quantity).reduce((a, b) => a + b, 0) || 0;

    return orderTotal + updateTotals;
  };

  updateOrder = async () => {
    // Prevent concurrent updates
    if (this.updatingOrder) {
      logger.warn('CartStore', 'Update already in progress, skipping duplicate call');
      return;
    }

    const update = this.orderUpdate.map(line => ({
      product_id: line.productId,
      quantity: line.quantity,
    }));

    runInAction(() => {
      this.updatingOrder = true;
    });

    try {
      // Add timeout wrapper to prevent infinite hanging
      const API_TIMEOUT = 10000; // 10 seconds timeout

      // NORMAL OPERATION (comment out when testing simulations):
      const data = await Promise.race([
        apiService.changeOrderLines(update),
        new Promise((_, reject) =>
          setTimeout(
            () => reject(new Error('Request timeout: API did not respond within 10 seconds')),
            API_TIMEOUT,
          ),
        ),
      ]);

      const giftCardsWithReadjustedChargeAmounts = readjustGiftCardsChargeAmounts(
        data,
        this.zenGiftCardsUserPaymentInstruments,
        this.switchingDeliveryPickup,
      );

      runInAction(() => {
        this.currentOrder = data;
        this.orderUpdate = [];
        this.dirtyOrder = false;
        this.updatingOrder = false;
        this.zenGiftCardsUserPaymentInstruments = giftCardsWithReadjustedChargeAmounts;
      });

      // Re-evaluate deal validity after cart change.
      // Called unconditionally — canApply values go stale for ALL offers when cart changes,
      // not just when a deal is currently applied. (DIS-1015 Fix #2)
      this.rootStore.offersStore.refreshOffers();
      // DIS-1608 trigger 1: the basket MFL just accepted may now cross the
      // coupon's minimum spend, in either direction.
      this.refreshCouponStatus();
    } catch (err: unknown) {
      // Check if it's a timeout error
      const isTimeout = err instanceof Error && err.message?.includes('timeout');

      runInAction(() => {
        // IMPORTANT: Always reset the updating flag first
        this.updatingOrder = false;

        // Don't clear the current order on timeout - keep the cart functional
        if (!isTimeout) {
          // For other errors, clear the dirty state but keep the order
          this.dirtyOrder = false;
          this.orderUpdate = [];
        }
      });

      // Show user-friendly error message
      if (isTimeout) {
        Toast.show({
          type: ERROR_MESSAGE,
          text1: 'Connection timeout. Please try again.',
        });
      } else {
        logger.error('CartStore', 'Cart update failed', serializeError(err));
        // Only reload for non-timeout errors
        this.loadOrderForUser();
      }
    }
  };

  updateOrderDirectly = async (
    orderUpdate: {
      product_id: string;
      quantity: number;
    }[],
  ) => {
    try {
      const data = await apiService.changeOrderLines(orderUpdate);

      const giftCardsWithReadjustedChargeAmounts = readjustGiftCardsChargeAmounts(
        data,
        this.zenGiftCardsUserPaymentInstruments,
        this.switchingDeliveryPickup,
      );

      runInAction(() => {
        this.currentOrder = data;
        this.orderUpdate = [];
        this.dirtyOrder = false;
        this.updatingOrder = false;
        this.zenGiftCardsUserPaymentInstruments = giftCardsWithReadjustedChargeAmounts;
      });

      // DIS-1608 trigger 2.
      this.refreshCouponStatus();
    } catch (_err) {
      logger.error('CartStore', 'Unable to update order line', serializeError(_err));
      runInAction(() => {
        this.dirtyOrder = false;
        this.orderUpdate = [];
        this.currentOrder = null;
        this.updatingOrder = false;
      });
      this
        .loadOrderForUser
        // TODO: this method doesn't actually receive any arguments...is the
        // store id really needed for something?
        // this.rootStore.shopSelectionStore.selectedStore.id,
        ();
    }
  };

  /**
   * Updates currentOrder directly from API response.
   * Used by TanStack Query mutations to sync state after successful cart updates.
   * Does NOT trigger debounce or set dirtyOrder flag.
   */
  setCurrentOrderFromResponse = (data: OrderResponse) => {
    const giftCardsWithReadjustedChargeAmounts = readjustGiftCardsChargeAmounts(
      data,
      this.zenGiftCardsUserPaymentInstruments,
      this.switchingDeliveryPickup,
    );

    runInAction(() => {
      this.currentOrder = data;
      this.orderUpdate = [];
      this.dirtyOrder = false;
      this.zenGiftCardsUserPaymentInstruments = giftCardsWithReadjustedChargeAmounts;
    });

    // DIS-1608 trigger 3: the TanStack mutation path. useCartMutation also calls
    // refreshCouponStatus directly, because its suite mocks this method away.
    this.refreshCouponStatus();
  };

  /**
   * DIS-1608: PATCH /ecommerce/coupon returning 200 does not mean the coupon will
   * be honoured. Validate is the only place MFL states the minimum spend. It
   * mutates nothing, so it is safe as a pre-check. Errors other than a coupon
   * refusal are ignored here — order_time_slot_blank is expected on the cart step.
   *
   * Fail-open by design: `parseCheckoutError` returns null for any status other
   * than 422, so a 500 or a network failure here yields no issue and the apply
   * reports success — a validate hiccup must not block a coupon the shopper may
   * be entitled to.
   *
   * Scope: this function answers exactly one question — is there a coupon
   * refusal. Surfacing other validate errors belongs in the checkout path, not
   * here, because `app/screens/cart/util/validation.ts` deliberately skips
   * server validation on the cart step and other details would put "time slot
   * is required" on the cart.
   */
  private checkCouponAccepted = async (): Promise<CartStore['couponIssue']> => {
    try {
      await apiService.validateOrder();
      return null;
    } catch (err) {
      const parsed = parseCheckoutError(err);
      const couponError = findCouponError(parsed?.details);
      if (!couponError) return null;
      return {
        code: couponError.code ?? null,
        detail: couponError.detail,
        orderSignature: this.couponOrderSignature,
      };
    }
  };

  /**
   * DIS-1608: re-ask MFL whether the coupon is honoured. Called by every order
   * writer after its write is confirmed — never optimistically — so validate is
   * always asked about a basket MFL has already accepted.
   *
   * Skips entirely when there is nothing MFL could refuse. Coalesces bursts.
   * Discards any response whose basket signature has moved, so a slow answer
   * cannot overwrite a fresher one.
   */
  refreshCouponStatus = ({immediate = false}: {immediate?: boolean} = {}) => {
    if (!this.currentOrder?.coupon_code && this.activatedDeals.length === 0) {
      if (this._couponCheckTimeout) clearTimeout(this._couponCheckTimeout);
      this._couponCheckTimeout = null;
      runInAction(() => {
        this.couponCheckPending = false;
      });
      return;
    }

    if (this._couponCheckTimeout) clearTimeout(this._couponCheckTimeout);
    runInAction(() => {
      this.couponCheckPending = true;
    });

    const run = async () => {
      const signatureAtRequest = this.couponOrderSignature;
      try {
        const issue = await this.checkCouponAccepted();
        if (this.couponOrderSignature !== signatureAtRequest) return; // stale
        // The check also runs for deal-only orders, where a coupon-shaped detail
        // has no coupon to belong to: nothing renders it and nothing clears it,
        // so holding it would only produce a toast about a coupon the shopper
        // does not have.
        if (!this.currentOrder?.coupon_code) return;
        // Read before the assignment, so a stale refusal discarded above cannot
        // masquerade as a crossing.
        const wasRefused = !!this.couponIssue;
        const isRefused = !!issue;
        runInAction(() => {
          this.couponIssue = issue;
          this.couponLastSettledQualified = !isRefused;
        });
        if (wasRefused && !isRefused) {
          // SUCCESS_TEXT, not SUCCESS_CHECK: the SUCCESS_CHECK toast renders a
          // tick icon only and drops text1 (ToastBottom.tsx), so the sentence
          // would never reach the shopper. Measured in a browser run.
          Toast.show({type: SUCCESS_TEXT, text1: COUPON_QUALIFIED_MESSAGE});
          if (Platform.OS === 'ios') {
            AccessibilityInfo.announceForAccessibility(COUPON_QUALIFIED_MESSAGE);
          }
        } else if (!wasRefused && isRefused) {
          Toast.show({type: ERROR_MESSAGE, text1: issue.detail});
          if (Platform.OS === 'ios') {
            AccessibilityInfo.announceForAccessibility(issue.detail);
          }
        }
      } finally {
        runInAction(() => {
          this.couponCheckPending = false;
        });
      }
    };

    if (immediate) {
      this._couponCheckTimeout = null;
      void run();
      return;
    }

    this._couponCheckTimeout = setTimeout(() => {
      this._couponCheckTimeout = null;
      void run();
    }, COUPON_CHECK_DEBOUNCE_TIME);
  };

  /**
   * Apply or remove a coupon, then reload the cart via loadOrderForUser so
   * order_lines keep enriched extra_prices/stickers (member tab + deal banners).
   * DIS-1517: do not write the PATCH /ecommerce/coupon body into currentOrder —
   * that payload is not a reliable cart-list source of truth.
   * DIS-1608: PATCH 200 does not mean MFL will honour the coupon at checkout, so
   * a validate pre-check runs after applying and the success toast is suppressed
   * when it refuses the coupon — the cart coupon row carries that message instead.
   */
  updateCoupon = async (coupon: string | null): Promise<CouponApplyResult> => {
    runInAction(() => {
      this.dirtyOrder = true;
      this.updatingOrder = true;
      // DIS-1608: the verdict held here belongs to whatever coupon was attached
      // before this call. Applying a different code must not inherit it, or the
      // new coupon's discount would be counted on trust for the whole check.
      this.couponLastSettledQualified = false;
      // Held from here, not from just before the pre-check: the apply and its
      // reload leave couponIssue null with the coupon attached, which couponStatus
      // reads as 'qualified'. Observers saw a tick on an unverified coupon for the
      // whole of both round trips.
      this.couponApplyInFlight = true;
    });
    try {
      const applyResponse = await apiService.updateCoupon(coupon);
      await this.loadOrderForUser();
      // Both branches below run their own check (trigger 4 for an apply, trigger 5
      // for a removal), so drop the debounced one that loadOrderForUser's trigger 8
      // just scheduled rather than paying for a second validate on every apply.
      this.cancelPendingCouponCheck();
      if (coupon === null) {
        runInAction(() => {
          this.couponIssue = null;
        });
        // DIS-1608 trigger 5: an applied deal may still be refusable, so re-judge.
        this.refreshCouponStatus();
        Toast.show({type: SUCCESS_CHECK});
        return {ok: true, qualified: true};
      }
      // DIS-1608: MFL's API reference documents /ecommerce/coupon returning a
      // non-null `rejection` string on the SAME 200 for cart-independent refusals
      // (e.g. "You've already used this coupon code"); minimum-spend and other
      // cart-dependent refusals are documented as validate-only, which is why
      // checkCouponAccepted() below still runs. A browser run against a simulated
      // MFL response measured `rejection` arriving at the top level of this
      // response. The nested `data.attributes.rejection` read is retained only
      // because no captured non-null example exists to rule that shape out.
      const rejection =
        (applyResponse as {rejection?: string | null})?.rejection ??
        (applyResponse as {data?: {attributes?: {rejection?: string | null}}})?.data?.attributes
          ?.rejection ??
        null;
      if (rejection) return await this.detachRefusedCoupon(rejection);

      // DIS-1608 trigger 4: no refreshCouponStatus call here — this validate IS
      // the check for the coupon just applied, and it is not debounced.
      const issue = await this.checkCouponAccepted();
      {
        if (!issue) {
          runInAction(() => {
            this.couponIssue = null;
            this.couponLastSettledQualified = true;
          });
          Toast.show({type: SUCCESS_CHECK});
          return {ok: true, qualified: true};
        }
        if (isCouponRefusalCode(issue.code ?? undefined)) {
          return await this.detachRefusedCoupon(issue.detail);
        }

        runInAction(() => {
          this.couponIssue = issue;
          this.couponLastSettledQualified = false;
        });
        // DIS-1608: the coupon row carries this persistently, but the row is not
        // guaranteed to be in view when the modal closes, so acknowledge the tap.
        // MFL's refusal sentence is shown verbatim and alone — the client adds no
        // wording of its own about what happens to the coupon next.
        Toast.show({
          type: ERROR_MESSAGE,
          text1: issue.detail,
        });
        // The toast sets no accessibility props and accessibilityLiveRegion on the
        // coupon row is Android-only, so iOS would otherwise announce nothing.
        if (Platform.OS === 'ios') {
          AccessibilityInfo.announceForAccessibility(issue.detail);
        }
        return {ok: true, qualified: false};
      }
    } catch (_err) {
      // DIS-1608: the apply threw, so the coupon already on the order is untouched
      // and its held refusal still belongs to it. Clearing unconditionally made
      // couponStatus fall through to 'qualified' and unblocked checkout on a coupon
      // MFL had not applied — measured in a browser run by submitting an unknown
      // code while a non-qualifying coupon was attached. Re-check rather than trust
      // the held issue, because an HTTP 500 leaves the server state unknown.
      const stillAttached = !!this.currentOrder?.coupon_code;
      if (!stillAttached) {
        runInAction(() => {
          this.couponIssue = null;
          this.couponLastSettledQualified = false;
        });
      } else {
        this.refreshCouponStatus();
      }
      logger.error('CartStore', 'Update coupon error', serializeError(_err));
      let errorMessage = "We couldn't apply your coupon. Please try again.";
      if (Array.isArray(_err.response?.data?.errors)) {
        errorMessage = _err.response.data.errors.map((e: {detail: string}) => e.detail).join('\n');
      } else if (isString(_err.response?.data) && isUnhandledMFL500Error(_err.response.data)) {
        // TMP: Coupons are important. We need to be able to quickly differentiate when MFL returns just HTTP 500
        // 2592-Coupons: Unhandled MFL error when adding coupon (https://dev.azure.com/lmgdevops/branded_website/_workitems/edit/2592)
        // In the meantime: ECOMM -> Ecommerce -> MFL, 500 -> HTTP 500
        errorMessage = `${errorMessage} (ECOMM-500)`;
      }
      Toast.show({type: ERROR_MESSAGE, text1: errorMessage});
      return {ok: false, message: errorMessage};
    } finally {
      runInAction(() => {
        this.updatingOrder = false;
        this.dirtyOrder = false;
        // Cleared last, after the verdict has been written, so couponStatus never
        // reads both flags false against a stale issue.
        this.couponApplyInFlight = false;
      });
    }
  };

  /**
   * DIS-1608: a coupon refusal — the coupon itself is not valid, so no amount of
   * shopping fixes it and it must not stay attached. Detaches it and hands MFL's
   * sentence back to the caller; no toast, because the entry modal stays open and
   * renders the message beside the code the shopper typed.
   */
  private detachRefusedCoupon = async (message: string): Promise<CouponApplyResult> => {
    // DIS-1608: `message` is MFL's own refusal sentence and is the only thing worth
    // telling the shopper. A throw in the detach or its reload used to unwind into
    // updateCoupon's catch, which replaced it with the generic "We couldn't apply
    // your coupon" — losing the reason while the dead coupon stayed attached.
    // Detaching is best-effort cleanup; the refusal is reported either way.
    try {
      await apiService.updateCoupon(null);
      await this.loadOrderForUser();
    } catch (err) {
      logger.error('CartStore', 'Detach refused coupon failed', serializeError(err));
    }
    // Nothing is left to validate, so drop the check trigger 8 scheduled on the way in.
    this.cancelPendingCouponCheck();
    runInAction(() => {
      this.couponIssue = null;
      this.couponLastSettledQualified = false;
    });
    return {ok: false, message};
  };

  /** Drops a debounced coupon check that a caller is about to make redundant. */
  private cancelPendingCouponCheck = () => {
    if (!this._couponCheckTimeout) return;
    clearTimeout(this._couponCheckTimeout);
    this._couponCheckTimeout = null;
    runInAction(() => {
      this.couponCheckPending = false;
    });
  };

  /**
   * Validates order and updates currentOrder (fire-and-forget version).
   * Use validateOrderAsync() when you need to wait for completion.
   */
  validateOrder = () => {
    this.validateOrderAsync();
  };

  /**
   * Validates order and updates currentOrder (async version).
   * Use this when you need to wait for the validation to complete before proceeding.
   *
   * @returns Promise that resolves when validation is complete
   */
  validateOrderAsync = async (): Promise<void> => {
    runInAction(() => (this.submittingOrder = true));
    try {
      // V2 endpoint returns validation status only, not full order data
      // Don't set currentOrder from response - it would wipe out order_lines
      await apiService.validateOrder();
      // Reload order to get fresh data after validation
      await this.loadOrderForUser();
    } catch (_err) {
      logger.error('CartStore', 'Unable to validate order', serializeError(_err));
      // Re-throw so callers know validation failed
      throw _err;
    } finally {
      runInAction(() => (this.submittingOrder = false));
    }
  };

  pollOrderStatus = (navigation: OrderProcessingScreenProps['navigation']) => {
    // Returned so callers/tests can await the poll cycle; OrderProcessingScreen
    // still calls this fire-and-forget (return value ignored).
    return apiService
      .pollOrder(this.currentOrder.id)
      .then(async (order: OrderResponse) => {
        // DIS-1441: pollOrderStatus is fired fire-and-forget every 2s and snapshots
        // currentOrder.id at dispatch. If currentOrder has since advanced (store switch /
        // reset+reload started a different order), this resolved poll is stale — dropping
        // it here, before any branch, prevents a stale poll from firing a second terminal
        // navigation (the DIS-1420 id-guard is nulled by reset() so it can't catch this)
        // and from clobbering the live order via the 3DS/failed/in-doubt branches.
        // Single-order flow is unchanged: pollOrder(id) returns that same id, so the
        // resolved order.id always matches the still-current order.
        if (order.id !== this.currentOrder?.id) {
          return;
        }
        const tenders = order.tenders || [];
        const isAllTendersCompletelySucceeded = tenders.every(
          tender => tender.succeeded && !tender.failed && !tender.in_doubt,
        );
        const isAtLeastOneTenderFailed = tenders.some(tender => tender.failed);
        const isNoneTenderFailed = !isAtLeastOneTenderFailed;
        const isAtLeastOneTenderInDoubtWithNoFailedTenders =
          isNoneTenderFailed && tenders.some(tender => tender.in_doubt);

        if (
          order.customer_interaction_data &&
          order.customer_interaction_data.redirect_url &&
          // DIS-1420: dedupe concurrent in-flight polls that both observe the 3DS redirect
          // before orderPayment3DSChallenged stops the interval — a second cross-navigator
          // THREEDS dispatch is the same reparent exposure as a duplicate terminal navigate.
          // The flag is set synchronously below, so only the first poll dispatches.
          !this.orderPayment3DSChallenged
        ) {
          // Order payment got challenged by 3DS
          runInAction(() => {
            this.currentOrder = order;
            this.orderPayment3DSChallenged = true;
          });
          // Navigate to 3DS screen using RootNavigation (React Navigation v7 best practice)
          // Per https://reactnavigation.org/docs/navigating-without-navigation-prop/
          // Store-based navigation should use navigationRef, not scoped navigation objects
          RootNavigation.navigate(AppRoute.ROOT, {
            screen: AppRoute.MAIN,
            params: {
              screen: AppRoute.THREEDS_IFRAME_FULLSCREEN,
              params: {
                url: order.customer_interaction_data.redirect_url,
                navigationTarget: AppRoute.CART_ORDER_PROCESSING,
              },
            },
          });
        } else if (
          order.checkout?.started_at &&
          order.checkout?.succeeded &&
          !order.checkout?.failed &&
          !order.checkout?.in_doubt &&
          order.order_number &&
          order.balance?.cents_inc_tax === 0 &&
          isAllTendersCompletelySucceeded
        ) {
          // DIS-1420: navigate-once guard. pollOrderStatus is fired fire-and-forget
          // every 2s; a poll that overlaps the prior poll's getCompetitionsForOrder
          // await must NOT run the terminal success path twice (duplicate Purchase
          // event + duplicate terminal navigation, which compounds the rn-screens
          // Fabric reparent crash). Synchronous check-and-set here — at the top of
          // the success branch, before the first await — makes it atomic. Keyed by
          // order id so a new checkout (different id) still navigates.
          // `!== null` so a degenerate null order.id on the first poll can't self-match
          // (null === null) and pre-empt the only navigation.
          if (this._navigatedOrderId !== null && this._navigatedOrderId === order.id) {
            return;
          }
          this._navigatedOrderId = order.id;

          Analytics.logEvent({
            eventName: EventName.Purchase,
            parameters: {
              screen_name: EventScreenName.Checkout,
              transaction_id: order.id,
              order_number: order?.order_number,
              value: order.total?.cents_inc_tax / 100,
              currency: 'AUD',
              tax: order.total?.cents_tax / 100,
              ...(order.total?.iso_currency_code && {
                currency_code: order.total?.iso_currency_code,
              }),
              ...(order?.coupon_code && {coupon: order.coupon_code}),
              items: order.order_lines.map(item => ({
                item_id: item.product_id,
                item_name: item.name,
                item_brand: item.brand_name ?? '',
                price: item.sell_price_cents / 100,
                quantity: item.quantity,
                discount: (item.normal_price_cents - item.sell_price_cents) / 100,
              })),
            },
          });

          // Order was successful in checking out
          // Check if the order is eligible for competition entry
          interface MflCompetition {
            id: string;
            name: string;
          }
          let comps: MflCompetition[] = [];
          try {
            comps = await apiService.getCompetitionsForOrder(order.id);
          } catch (_ex) {
            logger.error('CartStore', 'Failed checking competitions for order', {
              orderId: order.id,
              orderNumber: order.order_number,
              checkout: order.checkout,
              error: _ex,
            });
          }

          // DIS-1567: promotions come from the query cache and are FETCHED on a
          // cache miss — the old synchronous MobX read yielded [] when the store
          // had not loaded yet, and a completed order then silently entered no
          // competitions. Non-fatal on failure, matching the
          // getCompetitionsForOrder handling above: an empty list costs the
          // shopper a competition entry, but a throw here would abort the
          // enclosing `.then` and with it the order-confirmation navigation.
          let activePromotions: AirtablePromotionItem[] = [];
          try {
            // `rootStore` is nullable on this store; the previous expression
            // optional-chained it and fell back to [], so keep that.
            if (this.rootStore) {
              ({activePromotions} = await ensurePromotions(this.rootStore));
            }
          } catch (_ex) {
            logger.error('CartStore', 'Failed loading promotions for competition matching', {
              orderId: order.id,
              orderNumber: order.order_number,
              error: _ex,
            });
          }

          // Get a set of distinct activations that this order is eligible for.
          const eligibleCompetitionsSet = new Set<AirtablePromotionItem>();
          comps.forEach((comp: MflCompetition) => {
            const matched = activePromotions.filter((act: {activationUrlName: string}) => {
              const eligibleUrl = comp.name
                .replaceAll(' ', '-') // MFL identifier for competition uses spaces instead of hyphens
                .toLowerCase();
              return act.activationUrlName?.toLowerCase() === eligibleUrl;
            });

            matched.forEach((act: AirtablePromotionItem) =>
              eligibleCompetitionsSet.add({
                ...act,
                mflId: comp.id,
              }),
            );
          });
          let eligibleCompetitionsList = Array.from(eligibleCompetitionsSet);

          // Temporary: Sumer Cash Grab ----------------------------------
          // Place the Sumer Cash Grab competition at the bottom of the list
          const summerCashGrabCompetition = eligibleCompetitionsList.find(
            isSummerCashGrabCompetition,
          );
          if (summerCashGrabCompetition) {
            eligibleCompetitionsList = eligibleCompetitionsList.filter(
              comp => !isSummerCashGrabCompetition(comp),
            );
            // Add the Summer Cash Grab competition to the bottom of the list since the user can't go 'back' to promotions list
            eligibleCompetitionsList.push(summerCashGrabCompetition);
          }
          logger.debug('CartStore', 'Eligible competitions found', {
            competitionsListCount: eligibleCompetitionsList.length,
            competitionsSetSize: eligibleCompetitionsSet.size,
          });
          //----------------------------------------------------------------

          runInAction(() => {
            this.eligibleCompetitions = eligibleCompetitionsList;
            this.orderProcessing = false;
            this.currentOrder = order;
            this.orderCheckedOutSuccess = true;
          });
          if (eligibleCompetitionsSet.size) {
            logger.debug('CartStore', 'Navigating to competitions screen', {
              competitionsCount: eligibleCompetitionsSet.size,
            });
            // Navigate to competitions screen - use ROOT → MAIN hierarchy for cross-navigator compatibility
            // Use RootNavigation for store-based navigation (React Navigation v7 best practice)
            RootNavigation.navigate(AppRoute.ROOT, {
              screen: AppRoute.MAIN,
              params: {
                screen: AppRoute.CART_ORDER_COMPETITIONS,
              },
            });
          } else {
            if (this.rootStore?.shopSelectionStore.isSelectedStoreGiftCardStore) {
              navigation.navigate(AppRoute.GIFT_CARDS_MAIN, {
                screen: AppRoute.GIFT_CARDS_STORE_ORDER_CONFIRMATION,
              });
            } else {
              // Navigate to confirmation screen - use ROOT → MAIN hierarchy for cross-navigator compatibility
              // Use RootNavigation for store-based navigation (React Navigation v7 best practice)
              RootNavigation.navigate(AppRoute.ROOT, {
                screen: AppRoute.MAIN,
                params: {
                  screen: AppRoute.CART_ORDER_CONFIRMATION,
                },
              });
            }
          }
        } else if (
          order.checkout?.started_at &&
          order.checkout?.failed &&
          isAtLeastOneTenderFailed
        ) {
          // Order failed a payment
          runInAction(() => {
            this.currentOrder = order;
            this.orderFailedPayment = true;
          });
        } else if (
          order.checkout?.started_at &&
          order.checkout?.in_doubt &&
          isAtLeastOneTenderInDoubtWithNoFailedTenders
        ) {
          runInAction(() => {
            this.currentOrder = order;
            this.orderPaymentIncomplete = true;
          });
        }
      })
      .catch(_err => {
        logger.error('CartStore', 'Order processing error', serializeError(_err));
      });
  };

  checkoutOrder = async (
    // Accepted for signature compatibility but never read — checkoutOrder always
    // routes via RootNavigation. Nullable so callers without a nav object pass
    // `undefined` instead of casting `undefined as any`.
    navigation: OrderProcessingScreenProps['navigation'] | undefined,
    browserData: {
      java_enabled: boolean;
      language: string;
      color_depth: number;
      screen_height: number;
      screen_width: number;
      timezone: number;
    },
    paymentInstrumentId?: string,
    singleUsePaymentToken?: {payment_service_id: string; token: string},
  ): Promise<boolean> => {
    runInAction(() => {
      this.orderProcessing = true;
      this.submittingOrder = true;
    });
    try {
      // This will fail if for example there's no credit card registered in the
      // account and the tender options are not yet set:
      // await apiService.validateOrder();

      if (this.isGiftCardsBeingUsed) {
        await this.updateCurrentOrderGiftCardTenderOptions(paymentInstrumentId);
      }

      // Note: validateOrder() is called in handleContinue() BEFORE confirmation modal

      const checkoutRequest: {
        payment_instrument_id: string | undefined;
        single_use_payment_token?: {payment_service_id: string; token: string};
        browser_information: {
          java_enabled: boolean;
          language: string;
          color_depth: number;
          screen_height: number;
          screen_width: number;
          timezone: number;
        };
        capabilities?: string[];
      } = {
        payment_instrument_id: singleUsePaymentToken ? undefined : paymentInstrumentId,
        single_use_payment_token: singleUsePaymentToken,
        // Always include browser_information - required by API for all payment methods
        // iOS Apple Pay still needs device info for transaction processing (unlike web)
        browser_information: {...browserData},
      };

      // Tender Options - Returns 422 with tender_options capability and a payment instrument is sent
      // https://myfoodlink.github.io/mfl-smart-shopping-api-docs/shop__orders__validate/tender_options_-_returns_422_with_tender_options_capability_and_a_payment_instrument_is_sent.html
      if (this.isGiftCardsBeingUsed) {
        checkoutRequest.capabilities = ['tender_options'];
        checkoutRequest.payment_instrument_id = undefined;
      }

      // Type assertion needed: API accepts payment_instrument_id as optional when capabilities are present
      // The apiService.checkoutOrder type is incomplete - it doesn't include capabilities option
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await apiService.checkoutOrder(checkoutRequest as any);

      runInAction(() => {
        this.submittingOrder = false;
        // DIS-1220: order accepted — drop any stale expired-offer banner state.
        // Otherwise the flag persists into the next checkout attempt in-session.
        this.expiredOfferDetected = false;
        this.recoveryAttemptedThisSession = false;
      });
      // Navigate to processing screen - use ROOT → MAIN hierarchy for cross-navigator compatibility
      // (checkout flow is in ROOT_CHECKOUT_FLOW, but processing screen is in ROOT → MAIN)
      // Use RootNavigation for store-based navigation (React Navigation v7 best practice)
      RootNavigation.navigate(AppRoute.ROOT, {
        screen: AppRoute.MAIN,
        params: {
          screen: AppRoute.CART_ORDER_PROCESSING,
        },
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => (this.submittingOrder = false));

      const parsed = parseCheckoutError(err);

      if (parsed?.isRecoverable && parsed.recoveryAction === 'clear_deals') {
        // Prevent infinite loop - only allow recovery once per checkout session
        if (this.recoveryAttemptedThisSession) {
          logger.warn('CartStore', 'Recovery already attempted - showing error instead', {
            code: parsed.code,
          });
          Toast.show({
            type: ERROR_MESSAGE,
            text1: 'Unable to complete order. Your offer may no longer be valid.',
          });
          return false;
        }

        // Set flag for inline warning - OrderTotals will show banner
        logger.info('CartStore', 'Recoverable checkout error - offer expired', {
          code: parsed.code,
          message: parsed.message,
        });
        runInAction(() => {
          this.expiredOfferDetected = true;
          this.recoveryAttemptedThisSession = true;
        });
        return false;
      }

      // Non-recoverable or unknown error
      const message = parsed?.message || this.extractLegacyErrorMessage(err);
      Toast.show({
        type: ERROR_MESSAGE,
        text1: message || 'Unable to process order at this time.',
      });
      return false;
    } finally {
      this.rootStore?.resetOffersStore();
    }
  };

  /**
   * Extract error message from legacy error format
   */
  private extractLegacyErrorMessage(err: unknown): string | null {
    if (typeof err !== 'object' || err === null) return null;

    const axiosErr = err as {
      response?: {status?: number; data?: {errors?: {detail: string}[]} | string};
    };

    if (axiosErr.response?.status === 422) {
      if (typeof axiosErr.response.data === 'string') {
        return axiosErr.response.data;
      }
      if (axiosErr.response.data?.errors) {
        return axiosErr.response.data.errors.map(e => e.detail).join('\n');
      }
    }
    return null;
  }

  /**
   * Sets the expired offer detected state.
   * Called when validation detects an expired offer before checkout navigation.
   */
  setExpiredOfferDetected = (detected: boolean) => {
    runInAction(() => {
      this.expiredOfferDetected = detected;
    });
  };

  /**
   * Remove the activated deal from the order and refresh available offers.
   *
   * NOTE (DIS-1015 #7): The DELETE /ecommerce/order-deals endpoint clears ALL
   * activated deals, not a specific one — this is a backend constraint.
   * Currently acceptable because only one deal can be active at a time, but
   * will require per-deal removal if multi-deal support is ever added.
   */
  removeActivatedDeal = async () => {
    try {
      runInAction(() => {
        this.dirtyOrder = true;
      });
      await apiService.clearOrderDeals();
      runInAction(() => {
        this.activatedDeals = [];
        this.expiredOfferDetected = false;
      });
      await this.loadOrderForUser();
      await this.rootStore.offersStore.refreshOffers();
      // DIS-1608 triggers 6 and 7: a single deliberate tap, not a burst, and the
      // shopper is watching the total — so ask immediately.
      this.refreshCouponStatus({immediate: true});
    } catch (err) {
      runInAction(() => {
        this.dirtyOrder = false;
      });
      logger.error('CartStore', 'Failed to remove deal', serializeError(err));
      Toast.show({
        type: ERROR_MESSAGE,
        text1: 'Unable to remove offer. Please try again.',
      });
    }
  };

  /**
   * Apply an exclusive offer directly from the compact tile without navigating
   * to the detail screen. Mirrors removeActivatedDeal pattern.
   */
  applyOffer = async (mflId: string) => {
    try {
      runInAction(() => {
        this.dirtyOrder = true;
      });
      const zenGlobalId = this.rootStore.rewardsStore.cardNumber || undefined;
      const response = await apiService.applyOffer(mflId, zenGlobalId);
      runInAction(() => {
        this.activatedDeals = response.data.activated_deals;
        // DIS-1220: new deal applied — any prior expired-offer state is no longer relevant.
        this.expiredOfferDetected = false;
        this.recoveryAttemptedThisSession = false;
      });
      await this.loadOrderForUser();
      await this.rootStore.offersStore.refreshOffers();
      // DIS-1608 triggers 6 and 7: a single deliberate tap, not a burst, and the
      // shopper is watching the total — so ask immediately.
      this.refreshCouponStatus({immediate: true});
    } catch (err) {
      runInAction(() => {
        this.dirtyOrder = false;
      });
      logger.error('CartStore', 'Failed to apply offer', serializeError(err));
      Toast.show({
        type: ERROR_MESSAGE,
        text1: 'Unable to apply offer. Please try again.',
      });
    }
  };

  getProductsPromotedAtCheckout = () =>
    apiService
      .searchPromotedAtCheckout(
        this.rootStore?.shopSelectionStore.selectedStore?.promote_at_checkout_search?.id,
      )
      .then(res => {
        runInAction(() => (this.promotedAtCheckoutProducts = res));
      })
      .catch(_err => {
        logger.error(
          'CartStore',
          'Unable to search promoted at checkout products',
          serializeError(_err),
        );
        return null;
      });

  setOrderPayment3DSChallenged = (orderPayment3DSChallenged: boolean) => {
    this.orderPayment3DSChallenged = orderPayment3DSChallenged;
  };

  // Gift Cards
  setGiftCardRecipientFormValues = (giftCardRecipientFormValues: GiftCardFormData) =>
    (this.zenGiftCardRecipientFormValues = giftCardRecipientFormValues);

  addZenGiftCardsUserPaymentInstrument = async (
    cardNumber: string,
    pin: string,
    serviceId: string,
  ) => {
    const existingGiftCard = this.zenGiftCardsUserPaymentInstruments.find(
      card => card.zen_card_number === cardNumber,
    );
    if (existingGiftCard) {
      throw new Error('Gift card already added');
    }

    const request: CreateZenGiftCardPaymentInstrumentRequest = {
      order_id: this.currentOrder?.id,
      service_id: serviceId,
      zen_gift_card: {
        gift_card_number: cardNumber,
        gift_card_pin: pin,
      },
    };
    const response = (await apiService.createZenGiftCardPaymentInstrument(
      request,
    )) as unknown as CreateZenGiftCardPaymentInstrumentResponse;

    const paymentInstrument = response?.payment_instrument;
    if (!paymentInstrument) {
      throw new Error('Payment instrument not found');
    }

    const balance = paymentInstrument.balance || 0;
    if (balance === 0) {
      throw new Error('The gift card has no remaining balance');
    }

    // everything in cents
    const paymentInstrumentWithBalance: ZenGiftCardsPaymentInstrument = {
      ...paymentInstrument,
      balance,
    };

    const totalChargedOnGiftCards = this.totalChargedOnGiftCardsCents;
    const giftCardBalance = paymentInstrumentWithBalance.balance || 0;
    const orderTotalAmountRemaining = this.totalAmount - totalChargedOnGiftCards;
    const chargedAmount = Math.min(giftCardBalance, orderTotalAmountRemaining);
    const paymentInstrumentWithChargedAmount: ZenGiftCardsPaymentInstrumentWithChargedAmount = {
      ...paymentInstrumentWithBalance,
      chargedAmount,
    };

    runInAction(() =>
      this.zenGiftCardsUserPaymentInstruments.push(paymentInstrumentWithChargedAmount),
    );
  };

  deleteZenGiftCardsUserPaymentInstrument = (id: string) => {
    const remainingZenGiftCardsUserPaymentInstruments: ZenGiftCardsPaymentInstrumentWithChargedAmount[] =
      this.zenGiftCardsUserPaymentInstruments.filter(card => card.id !== id);

    const giftCardsWithReadjustedChargeAmounts = readjustGiftCardsChargeAmounts(
      this.currentOrder,
      remainingZenGiftCardsUserPaymentInstruments,
      this.switchingDeliveryPickup,
    );

    runInAction(() => {
      this.zenGiftCardsUserPaymentInstruments = giftCardsWithReadjustedChargeAmounts;
    });
  };

  clearCurrentOrderTenderOptions = async () =>
    apiService.updateCurrentOrderTenderOptions({order: {tender_options: []}});

  updateCurrentOrderGiftCardTenderOptions = async (paymentInstrumentId?: string) => {
    const isGiftCardStore = this.rootStore?.shopSelectionStore.isSelectedStoreGiftCardStore;

    if (isGiftCardStore) {
      return;
    }

    const giftCardsWithChargedAmount = this.zenGiftCardsUserPaymentInstruments.filter(
      card => card.chargedAmount > 0,
    );
    const tenderOptions = giftCardsWithChargedAmount.map(card => ({
      payment_instrument_id: card.id,
      amount_to_charge: {
        type: 'up_to',
        amount: card.chargedAmount, // cents
      },
    }));

    const request = {
      order: {
        tender_options: tenderOptions,
      },
    };

    if (tenderOptions.length) {
      const totalChargedOnGiftCards = this.totalChargedOnGiftCardsCents;
      const orderTotalAmountRemaining = this.totalAmount - totalChargedOnGiftCards; // cents

      if (orderTotalAmountRemaining > 0 && paymentInstrumentId) {
        request.order.tender_options.push({
          payment_instrument_id: paymentInstrumentId,
          amount_to_charge: {
            type: 'remainder',
            amount: orderTotalAmountRemaining,
          },
        });
      }
    }

    await apiService.updateCurrentOrderTenderOptions(request);
  };

  // Taken from OrderTotals.tsx. That need to be refactored but I'm afraid to
  // touch that at this moment. Critical area that has not been changed
  // since the original version.
  getCartSubtotal = (): number => calculateSubtotal(this.currentOrder?.order_lines);

  getCartGstIncluded = () => this.currentOrder?.total?.cents_tax || 0;

  getCartAdjustments = () =>
    getNonDeliveryAdjustments(this.currentOrder?.adjustments as OrderAdjustment[] | undefined);

  getCartDiscountsAndCredits = () =>
    getDiscountsAndCredits(this.currentOrder?.discounts_and_credits);

  get totalChargedOnGiftCardsCents() {
    return this.zenGiftCardsUserPaymentInstruments.reduce(
      (acc, card) => acc + card.chargedAmount,
      0,
    );
  }

  get isGiftCardsBeingUsed() {
    return (
      this.zenGiftCardsUserPaymentInstruments.length > 0 && this.totalChargedOnGiftCardsCents > 0
    );
  }

  /**
   * Remaining balance after gift cards are applied (in cents).
   * Used for Apple Pay split payment to show correct amount on payment sheet.
   */
  get remainingBalanceAfterGiftCardsCents(): number {
    return Math.max(0, this.totalAmount - this.totalChargedOnGiftCardsCents);
  }
}
